import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sidebar, MobileNav } from '@client/components';
import { Utils } from '@client/utility';
import type { User } from '@supabase/supabase-js';
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Container,
  Paper,
  Fade,
  Slide,
} from '@mui/material';
import { PyrenzBlueButton } from '@client/theme';
import { Account, Profile, Persona, Api, Customization } from './items';
import { useLocation, useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import FaceIcon from '@mui/icons-material/Face';
import CodeIcon from '@mui/icons-material/Code';
import PaletteIcon from '@mui/icons-material/Palette';

const tabs = ['account', 'profile', 'persona', 'api', 'customization'] as const;

const tabIcons = {
  account: <PersonIcon fontSize="small" />,
  profile: <SettingsIcon fontSize="small" />,
  persona: <FaceIcon fontSize="small" />,
  api: <CodeIcon fontSize="small" />,
  customization: <PaletteIcon fontSize="small" />,
};

export function Setting() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));

  const searchParams = new URLSearchParams(location.search);
  const urlTab = searchParams.get('tab') as (typeof tabs)[number] | null;

  const [activeTab, setActiveTab] = useState<(typeof tabs)[number]>(
    tabs.includes(urlTab as any) ? (urlTab as any) : 'account'
  );
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (urlTab && tabs.includes(urlTab as any)) setActiveTab(urlTab as any);
  }, [urlTab]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const { data: sessionData, error: sessionError } =
        await Utils.db.client.auth.getSession();
      if (sessionError || !sessionData.session) {
        setLoading(false);
        return;
      }
      const { data: userData, error: userError } =
        await Utils.db.client.auth.getUser();
      if (!userError && userData?.user) setUser(userData.user);
      setLoading(false);
    };
    init();
  }, []);

  const TabSidebarDesktop = () => (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: 240,
        flexShrink: 0,
        p: 2,
        borderRadius: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        height: 'fit-content',
        position: 'sticky',
        top: 24,
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: 'rgba(255, 255, 255, 0.5)',
          fontWeight: 600,
          letterSpacing: '2px',
          mb: 1,
          pl: 1,
        }}
      >
        Settings
      </Typography>
      {tabs.map((tab) => {
        const isActive = activeTab === tab;
        return (
          <PyrenzBlueButton
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              navigate(`?tab=${tab}`, { replace: true });
            }}
            sx={{
              justifyContent: 'flex-start',
              px: 2,
              py: 1.5,
              borderRadius: 2,
              color: isActive ? '#667eea' : 'rgba(255, 255, 255, 0.7)',
              backgroundColor: isActive
                ? 'rgba(102, 126, 234, 0.15)'
                : 'transparent',
              border: 'none',
              fontWeight: isActive ? 600 : 500,
              fontSize: '0.9rem',
              letterSpacing: '0.3px',
              transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': isActive ? {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 3,
                height: 16,
                backgroundColor: '#667eea',
                borderRadius: '0 4px 4px 0',
              } : {},
              '&:hover': {
                backgroundColor: isActive
                  ? 'rgba(102, 126, 234, 0.2)'
                  : 'rgba(255, 255, 255, 0.08)',
                color: isActive ? '#667eea' : '#fff',
                transform: 'translateX(4px)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 1 }}>
              <Box
                sx={{
                  color: isActive ? '#667eea' : 'rgba(255, 255, 255, 0.5)',
                  transition: 'color 200ms',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {tabIcons[tab]}
              </Box>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Box>
          </PyrenzBlueButton>
        );
      })}
    </Paper>
  );

  const TabSidebarMobile = () => (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        overflowX: 'auto',
        pb: 2,
        mb: 2,
        width: '100%',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab;
        return (
          <PyrenzBlueButton
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              navigate(`?tab=${tab}`, { replace: true });
            }}
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1,
              borderRadius: 2,
              color: isActive ? '#667eea' : 'rgba(255, 255, 255, 0.7)',
              backgroundColor: isActive
                ? 'rgba(102, 126, 234, 0.15)'
                : 'rgba(255, 255, 255, 0.04)',
              border: 'none',
              fontWeight: isActive ? 600 : 500,
              fontSize: '0.85rem',
              minWidth: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {tabIcons[tab]}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Box>
          </PyrenzBlueButton>
        );
      })}
    </Box>
  );

  const Content = () => {
    switch (activeTab) {
      case 'account':
        return <Account />;
      case 'profile':
        return <Profile />;
      case 'persona':
        return <Persona />;
      case 'api':
        return <Api />;
      case 'customization':
        return <Customization />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
        display: 'flex',
      }}
    >
      <Sidebar />
      {isMedium && <MobileNav setShowLoginModal={() => {}} />}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { md: '72px' },
          minHeight: '100vh',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: 4,
            px: { xs: 2, md: 4 },
          }}
        >
          <Fade in timeout={600}>
            <Box
              display="flex"
              flexDirection={isMedium ? 'column' : 'row'}
              alignItems="flex-start"
              gap={3}
            >
              {isMedium ? <TabSidebarMobile /> : <TabSidebarDesktop />}

              <Box
                flexGrow={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
                width="100%"
                maxWidth={isMedium ? '100%' : 'calc(100% - 280px)'}
              >
                <Slide direction="up" in timeout={400}>
                  <Paper
                    elevation={0}
                    sx={{
                      width: '100%',
                      maxWidth: '800px',
                      p: { xs: 3, md: 5 },
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    }}
                  >
                    {loading ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="300px"
                      >
                        <CircularProgress sx={{ color: '#667eea' }} />
                      </Box>
                    ) : !user && activeTab === 'account' ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="300px"
                      >
                        <Typography
                          variant="h6"
                          sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                        >
                          Please log in to access your account settings.
                        </Typography>
                      </Box>
                    ) : (
                      <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                      >
                        <Content />
                      </motion.div>
                    )}
                  </Paper>
                </Slide>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
}